export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

type PendingResolver<T> = {
  resolve: (value: T | undefined) => void
  reject: (error: unknown) => void
}

export type BatchLoaderOptions<T> = {
  /** Fetches one batch of ids. An id missing from the result resolves `undefined`. */
  fetch: (ids: string[]) => Promise<Map<string, T>>
  /** How long to collect ids before the first fetch. */
  batchWindowMs: number
  /** The most ids one fetch may name; should match the endpoint's own cap. */
  maxBatchSize: number
}

/**
 * Batches single-id loads into fetches of up to `maxBatchSize` ids. Ids requested within
 * `batchWindowMs` of each other share a fetch, and fetches run one at a time, never in parallel.
 */
export function createBatchLoader<T>({fetch, batchWindowMs, maxBatchSize}: BatchLoaderOptions<T>): {
  load: (id: string) => Promise<T | undefined>
} {
  // Ids that arrive while a fetch is in flight are picked up by the next loop iteration, so a
  // slow request absorbs everything queued behind it instead of firing overlapping requests.
  let pending = new Map<string, Array<PendingResolver<T>>>()
  let scheduled = false
  let draining = false

  function scheduleDrain(): void {
    // While draining, the loop already picks up newly-queued ids, so no new timer.
    if (scheduled || draining) return
    scheduled = true
    setTimeout(() => {
      scheduled = false
      void drain()
    }, batchWindowMs)
  }

  async function drain(): Promise<void> {
    if (draining) return
    draining = true
    try {
      while (pending.size > 0) {
        const batch = pending
        pending = new Map()
        for (const chunkIds of chunk([...batch.keys()], maxBatchSize)) {
          try {
            const byId = await fetch(chunkIds)
            for (const id of chunkIds) {
              for (const resolver of batch.get(id) ?? []) resolver.resolve(byId.get(id))
            }
          } catch (error) {
            // Isolate failures per chunk so one bad request doesn't reject the rest.
            for (const id of chunkIds) {
              for (const resolver of batch.get(id) ?? []) resolver.reject(error)
            }
          }
        }
      }
    } finally {
      draining = false
    }
  }

  return {
    load(id: string): Promise<T | undefined> {
      return new Promise<T | undefined>((resolve, reject) => {
        const resolvers = pending.get(id) ?? []
        resolvers.push({resolve, reject})
        pending.set(id, resolvers)
        scheduleDrain()
      })
    },
  }
}
